<template>
  <div class="user-editor-page" v-rect-loading="loading">
    <div class="user-editor-header">
      <div class="user-editor-title">{{ titleLabel }}</div>
    </div>
    <div class="form-container">
      <WrappedFrom
        ref="form"
        class="user-management-info"
        label-width="220px"
        :model="userData"
        :formLines="formLines"
      >
        <template #companyId="{ lineName, line }">
          <custom-remote-el-select
            v-model="userData[lineName]"
            v-bind="line.props"
            filterable
            :name="lineName"
            :placeholder="line.placeholder"
          >
            <el-option
              v-for="(option, index) in line.option.data"
              :key="index"
              :label="line.option.labelKey ? option[line.option.labelKey] : option.label"
              :value="line.option.valueKey ? option[line.option.valueKey] : option.value"
              :style="{ paddingLeft: `${option.level * 15}px` }"
            />
          </custom-remote-el-select>
        </template>
      </WrappedFrom>
      <div class="reset-password-btn">
        <a v-if="isEditMode" @click="handleResetPassword">{{$t(`${i18nPrefix}.btn.resetPassword`)}}</a>
      </div>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ cancelBtnLabel }}</el-button>
        <el-button class="save-btn" @click="handleSave">{{ saveBtnLabel }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { selectLabelI18n, selectFuncLabelI18n, addI18nProperty, isEn } from '@/i18n'
import { mapActions } from 'vuex'
import { mixedRouterPush } from '@/router/routes'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import { validateEmail } from '@/utils/validator'
export default {
  components: {
    WrappedFrom
  },
  data () {
    return {
      i18nPrefix: 'admin.company.userManagement',
      loading: false,
      loadFirst: true,
      userData: {},
      isEn,
      isShowPermission: false,
      companyList: [],
      programTypeList: [],
      roleTypeList: {},
      reportToList: [],
      queryCompany: null,
      selectLabelI18n,
      selectFuncLabelI18n
    }
  },
  computed: {
    isEditMode () {
      return this.$route.meta.isEditMode
    },
    accountId () {
      return this.$route.params.accountId
    },
    titleLabel () {
      return this.$t(`${this.i18nPrefix}${this.isEditMode ? '.label.editUser' : '.label.addUser'}`)
    },
    cancelBtnLabel () {
      return selectLabelI18n(this, 'cancelBtn', 'common.btn.cancel')
    },
    saveBtnLabel () {
      return selectLabelI18n(this, 'saveBtn', 'common.btn.save')
    },
    formLines () {
      const lines = {
        first_name: {
          label: this.$t(`${this.i18nPrefix}.label.firstName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.firstName`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.userRequired`), trigger: 'blur' }]
        },
        last_name: {
          label: this.$t(`${this.i18nPrefix}.label.lastName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.lastName`),
          rules: [{ required: true, message: this.$t(`${this.i18nPrefix}.message.userRequired`), trigger: 'blur' }]
        },
        chinese_name: {
          hidden: !this.isShowPermission,
          label: this.$t(`${this.i18nPrefix}.label.chineseName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.chineseName`)
        },
        company_id: {
          contentSlot: 'companyId',
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.company`),
          option: {
            data: this.companyList,
            valueKey: 'id',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.company`)
        },
        email_address: {
          label: this.$t(`${this.i18nPrefix}.label.${this.isShowPermission ? 'primaryEmail' : 'email'}`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.${this.isShowPermission ? 'primaryEmail' : 'email'}`),
          props: { type: 'input', autosize: { minRows: 5 } },
          rules: [
            { required: true, message: this.$t(`${this.i18nPrefix}.message.userRequired`), trigger: 'blur' },
            { validator: validateEmail, trigger: 'blur' },
            { validator: this.validateEmailAddress, trigger: 'blur' }
          ]
        },
        secondary_email_address: {
          hidden: !this.isShowPermission,
          label: this.$t(`${this.i18nPrefix}.label.secondaryEmail`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.secondaryEmail`)
        },
        mobile: {
          label: this.$t(`${this.i18nPrefix}.label.mobile`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.mobile`),
          rules: [{ validator: this.validateMobile, trigger: 'blur' }]
        },
        employee_id: {
          hidden: !this.isShowPermission,
          label: this.$t(`${this.i18nPrefix}.label.employeeId`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.employeeId`)
        },
        report_to: {
          hidden: !this.isShowPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.reportTo`),
          option: {
            data: this.reportToList,
            valueKey: 'id',
            labelKey: 'email'
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.reportTo`),
          props: { multiple: true, filterable: true }
        },
        program_type: {
          hidden: !this.isShowPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.programType`),
          option: {
            data: this.programTypeList,
            valueKey: 'value',
            labelKey: 'value'
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.programType`),
          props: { filterable: true }
        },
        client_name: {
          hidden: !this.isShowPermission,
          label: this.$t(`${this.i18nPrefix}.label.clientName`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.clientName`)
        },
        cost_centre: {
          hidden: !this.isShowPermission,
          label: this.$t(`${this.i18nPrefix}.label.costCentre`),
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.costCentre`)
        },
        role_type: {
          hidden: !this.isShowPermission,
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.roleType`),
          option: {
            data: this.roleTypeList,
            valueKey: 'name',
            labelKey: `${isEn() ? 'name' : 'cn_name'}`
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.roleType`),
          props: { filterable: true }
        }
      }
      if (!this.isEditMode) {
        lines.role_id = {
          elType: 'el-select',
          label: this.$t(`${this.i18nPrefix}.label.role`),
          option: {
            data: this.roleList,
            valueKey: 'id',
            labelKey: 'title'
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.role`),
          props: { filterable: true }
        }
      }
      return lines
    },
    form () {
      const form = this.$refs.form
      return form
    }
  },
  created () {
    this.queryCompany = this.$route.query.company
    this.initData()
  },
  methods: {
    ...mapActions('userManagement', ['getUser', 'saveUser', 'updateUser', 'resetPassword', 'userEmailValidate', 'userMobileValidate', 'getUserDefaultInfo']),
    ...mapActions('company', ['getCompanyChildrenTree']),
    async initData () {
      this.loading = true
      try {
        const companyTreeResp = await this.getCompanyChildrenTree()
        const currentCompany = {
          id: companyTreeResp.self.id,
          name: companyTreeResp.self.name,
          cn_name: companyTreeResp.self.cn_name,
          level: companyTreeResp.self.level
        }
        this.selectCompany = currentCompany.id
        const companyList = []
        this.companyList = this.formatCompanyData(companyTreeResp, currentCompany, companyList)
        await this.getSelectOptionDefaultInfo()
        if (this.isEditMode) {
          const params = {
            account_id: this.accountId,
            company_id: this.selectCompany
          }
          const resp = await this.getUser(params)
          this.userData = resp
        } else {
          this.userData = {
            company_id: parseInt(this.queryCompany),
            program_type: this.programTypeList[0]?.value,
            role_type: this.roleTypeList[0]?.name,
            role_id: this.roleList[0]?.id
          }
        }
      } finally {
        this.loading = false
      }
    },
    async getSelectOptionDefaultInfo () {
      const resp = await this.getUserDefaultInfo()
      this.isShowPermission = resp.is_show_permission
      this.programTypeList = this.formatSelectOptionList(resp.program_type)
      this.roleTypeList = _.map(resp.role_type.name, (name, index) => ({
        name: name,
        cn_name: resp.role_type.cn_name[index]
      }))
      this.reportToList = resp.report_to_list
      this.roleList = resp.all_role
    },
    async submit () {
      let resp
      if (this.isEditMode) {
        const params = {
          account_id: this.accountId,
          account: this.userData
        }
        resp = await this.updateUser(params)
      } else {
        resp = await this.saveUser(this.userData)
      }
      if (resp) {
        this.$message.success(this.$t(`${this.i18nPrefix}.message.optionSuccess`))
        this.$router.replace({ path: '/v-admin/user/management/list', query: { company: this.queryCompany, order: '-updated_time', keywords: this.$route.query.keywords } })
      } else {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.optionField`))
      }
    },
    async handleResetPassword () {
      if (this.loading) {
        return
      }
      this.loading = true
      const params = {
        account_id: this.accountId
      }
      try {
        const resp = await this.resetPassword(params)
        if (resp) {
          if (resp.send_limit) {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.resetPasswordError`))
          } else {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.resetPasswordSuccess`))
          }
        }
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      if (this.loading) {
        return
      }
      this.loading = true
      try {
        let result = false
        result = await this.form.submit()
        if (!result) {
          return
        }
        await this.submit()
      } finally {
        this.loading = false
      }
    },
    async validateEmailAddress (rule, value, callback) {
      const params = {
        edit_account_id: this.isEditMode ? this.accountId : null,
        email: value
      }
      const resp = await this.userEmailValidate(params)
      if (resp) {
        if (resp.validate) {
          callback()
        } else {
          callback(new Error(this.$t(`${this.i18nPrefix}.message.emailExist`)))
        }
      }
    },
    async validateMobile (rule, value, callback) {
      if (!value) {
        return callback()
      }
      const params = {
        edit_account_id: this.isEditMode ? this.accountId : null,
        mobile: value
      }
      const resp = await this.userMobileValidate(params)
      if (resp) {
        if (resp.validate) {
          callback()
        } else {
          callback(new Error(this.$t(`${this.i18nPrefix}.message.mobileExist`)))
        }
      }
    },
    formatCompanyData (data, company, companyList) {
      addI18nProperty(company, 'i18nName', 'cn_name', 'name')
      companyList.push(company)
      const allChildren = data.children
      const children = allChildren[company.id]
      if (children && children.length > 0) {
        for (const child of children) {
          this.formatCompanyData(data, child, companyList)
        }
      }
      return companyList
    },
    formatSelectOptionList (data) {
      return _.map(data, (item) => {
        return {
          value: item
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.user-editor-page {
  .user-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;

    .user-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }

  .form-container {
    width: 606px;
    margin: 30px auto 0;
  }

  .reset-password-btn {
    margin-left: 220px;
    a {
      color: #0096cc;
      text-decoration: underline;
      cursor: pointer;
    }
  }

  .bottom-btn-group {
    display: flex;
    justify-content: flex-end;

    .el-button {
      padding: 8px 28px;
      border-radius: 2px;
      line-height: 22px;
      font-size: 16px;
      font-weight: 500;
      color: #0096cc;

      &:last-child {
        margin-left: 24px;
        margin-right: 0;
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;
      }
    }
  }
}
</style>
